import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme, type ThemePreference } from '../context/ThemeContext';

const OPTIONS: { value: ThemePreference; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

interface ThemeToggleProps {
  /** `desktop` renders a compact cycling icon button for the header;
   *  `mobile` renders a labelled segmented control for open menus. */
  variant: 'desktop' | 'mobile';
}

/** Theme switcher. Reachable logged out, since the public directory and
 *  booking flow are anonymous. */
export default function ThemeToggle({ variant }: ThemeToggleProps) {
  const { preference, setPreference } = useTheme();

  if (variant === 'desktop') {
    const index = OPTIONS.findIndex((o) => o.value === preference);
    const current = OPTIONS[index] ?? OPTIONS[2];
    const next = OPTIONS[(index + 1) % OPTIONS.length] ?? OPTIONS[0];
    const { Icon } = current;

    return (
      <button
        onClick={() => setPreference(next.value)}
        className="hover:text-accent hover:bg-muted text-fg-secondary cursor-pointer rounded-lg p-2 transition-colors"
        title={`Theme: ${current.label} — switch to ${next.label}`}
        aria-label={`Theme: ${current.label}. Switch to ${next.label}.`}
      >
        <Icon size={18} />
      </button>
    );
  }

  return (
    <div>
      <p className="text-fg-muted mb-2 text-xs font-semibold tracking-wide uppercase">Theme</p>
      <div className="border-default flex gap-1 rounded-xl border p-1">
        {OPTIONS.map(({ value, label, Icon }) => {
          const active = preference === value;
          return (
            <button
              key={value}
              onClick={() => setPreference(value)}
              aria-pressed={active}
              className={`flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                active ? 'bg-brand-600 text-white' : 'hover:bg-muted text-fg-secondary'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
